import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import './i18n';
import './index.css';
import App from './App';
import { AuthProvider } from '@/features/auth/AuthContext';
import { ToastProvider } from '@/components/ui/Toast';
import { queryClient } from '@/lib/queryClient';

/**
 * The app is a PWA, so an open tab keeps running the bundle its service worker
 * cached until something reloads it. `registerType: 'autoUpdate'` makes the new
 * worker skip waiting and claim clients, but the page already on screen still
 * executes the old JavaScript — which is how a shipped fix can look like it was
 * never deployed. Reload once the new worker takes control, and poll hourly so a
 * phone left open on site picks up a deploy without anyone being told to.
 */
if ('serviceWorker' in navigator) {
  let reloading = false;

  // On a first visit there is no controller yet, so `clientsClaim` fires
  // controllerchange once just to take over — reloading on that would bounce
  // every new visitor (and loop while the cache is cold). Only an update, where
  // a controller was already in place, is worth a reload.
  const wasControlled = Boolean(navigator.serviceWorker.controller);

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!wasControlled || reloading) return;
    reloading = true;
    window.location.reload();
  });

  void navigator.serviceWorker.ready.then((registration) => {
    setInterval(() => void registration.update(), 60 * 60 * 1000);
  });
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ToastProvider>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </ToastProvider>
      </AuthProvider>
    </QueryClientProvider>
  </StrictMode>,
);
